import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { formatDateTime } from '@/helpers/formatting';
import { MembershipStatusBadge } from '@/components/MembershipStatusBadge';
import { IconEdit } from '@/icons';
import { cn } from '@/helpers/classnames';
import type {
  TravellerMembershipDTO,
  CollectionMemberDTO,
} from '@/api/trafikportalen';
import { StudentMembershipStatusActions } from './StudentMembershipStatusActions';
import { WarningBanner } from '../core/WarningBanner';

interface StudentMembershipInfoProps {
  membership: TravellerMembershipDTO;
  collectionMember?: CollectionMemberDTO;
  action?: ReactNode;
  travellerId: string;
}

export const StudentMembershipInfo = ({
  membership,
  collectionMember,
  action,
  travellerId,
}: StudentMembershipInfoProps) => {
  return (
    <div className="flex flex-col gap-x-4 gap-y-2 rounded-lg bg-background-100 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4">
        <MembershipStatusBadge membership={membership} />

        <StudentMembershipStatusActions
          membership={membership}
          travellerId={travellerId}
          collectionId={membership.collection_id}
        />

        {collectionMember && (
          <div
            className={cn(
              'flex flex-wrap items-center gap-x-3 text-sm text-text-700',
              !action && 'ml-auto',
            )}
          >
            <span>
              <FormattedMessage defaultMessage="Tillagd" />{' '}
              {formatDateTime(collectionMember.created_at)}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              <FormattedMessage defaultMessage="Uppdaterad" />{' '}
              {formatDateTime(collectionMember.updated_at)}
            </span>
          </div>
        )}

        {action && <div className="ml-auto">{action}</div>}
      </div>

      <p>
        <FormattedMessage defaultMessage="Nedan är den info som är unik för denna kategorin. Övrig information finns i fliken 'Resenär'." />
      </p>

      {collectionMember?.manually_modified && (
        <WarningBanner announce={false} icon={<IconEdit />}>
          <FormattedMessage
            id="import.manuallyModified.title"
            defaultMessage="Manuellt ändrad"
          />
          .{' '}
          <FormattedMessage
            id="import.manuallyModified.description"
            defaultMessage="Datan har blivit manuellt redigerad och kan komma att skrivas över vid import om inte samma ändring(ar) finns med i import-filen."
          />
        </WarningBanner>
      )}
    </div>
  );
};
